import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api.js";
import type {
  JobListEntry,
  Location,
  ManagedTaskType,
  Employee,
  TaskVisibility,
} from "../types.js";
import { splitInvoices } from "../lib/invoices.js";
import { Icon } from "../components/Icon.js";
import { Sheet } from "../components/Sheet.js";

/**
 * Everything a lead does to tasks, on one screen, in two halves that do
 * different things and must not look alike.
 *
 * **Custom tasks** is the daily work: start one for a cart, get its tag,
 * finish it when the cart is empty. **Task types** is setup: the kinds of
 * work that exist, which change a few times a month. Until 2026-09-26
 * the two were forms stacked on one page — "Add a task" above "Start a
 * periodical task" — and the shop found they read as the same action.
 * They are now separate tabs, and each form sits behind its own button in
 * its own sheet, so the page is the list and the action is named.
 *
 * The catalogue and the running work used to be two screens, one
 * reached from the other, and nobody could say which was which: the
 * outer one was called "Jobs" and the inner one "Tasks" for the same
 * subject matter. Merged on 2026-09-07, and the word "job" left the
 * interface with them. It stays in the schema, the API and SPEC.md —
 * `Job` is a row, and renaming it would mean a migration and a rewrite
 * of `job_metrics` to gain nothing the shop can see.
 *
 * Nothing here deletes. Retiring a task sets it inactive: it stops being
 * offered, and everything already recorded against it stays. Deleting
 * would delete the measurements the tool exists for.
 *
 * People used to live here too, behind a second tab. They moved to the
 * dashboard on 2026-09-07 and with them the ADMIN restriction — see
 * PeoplePanel. Leads keep the catalogue, because retiring a task nobody
 * does any more is ordinary shop-floor work.
 */
export function TasksScreen({
  me,
  onBack,
  onJobsChanged,
}: {
  /** Who is looking. Scoping a task is admin-only; the catalogue is not. */
  me: Employee;
  onBack: () => void;
  // Finishing a task stops sessions, possibly this lead's own, so the
  // banner has to be told to re-check.
  onJobsChanged: () => void;
}) {
  const [taskTypes, setTaskTypes] = useState<ManagedTaskType[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [jobs, setJobs] = useState<JobListEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Not everything worth saying is a failure — the same distinction
  // WorkScreen makes. A revival is the tool working correctly.
  const [notice, setNotice] = useState<string | null>(null);
  // Who sees one task. The other view of the same table is Admin →
  // Activities, which answers it per person; this one answers it per
  // task, which is the question an admin has the moment they add
  // something only a few people do.
  const [seenBy, setSeenBy] = useState<ManagedTaskType | null>(null);
  const [people, setPeople] = useState<TaskVisibility[] | null>(null);
  const [busy, setBusy] = useState(false);

  // One sheet for a task type, new or edited. The kind is chosen once,
  // at creation: a task that changed shape halfway through makes its own
  // numbers incomparable, so editing shows it and does not offer it.
  const [typeSheet, setTypeSheet] = useState<
    { mode: "new" } | { mode: "edit"; task: ManagedTaskType } | null
  >(null);
  const [typeForm, setTypeForm] = useState({
    name: "",
    kind: "STANDING" as "JOB" | "STANDING",
    description: "",
  });
  // Retired tasks are hidden until asked for. A shop that has been
  // running a year has more retired tasks than live ones, and the list
  // is read at arm's length.
  const [showRetired, setShowRetired] = useState(false);

  const [starting, setStarting] = useState(false);
  const [taskTypeId, setTaskTypeId] = useState("");
  const [locationId, setLocationId] = useState("");
  // Free text, split on submit. A lead reads numbers off a delivery note
  // or pastes a column out of the invoice sheet, and both arrive with
  // whatever separators that source used.
  const [invoiceText, setInvoiceText] = useState("");
  const [created, setCreated] = useState<{ id: string; label: string } | null>(null);
  // Correcting an open cart's invoice numbers — what the start sheet
  // promises. They are a join key against the shop's invoice sheet, so a
  // typo joins to nothing without saying so; until 2026-09-26 the route
  // existed and no screen called it.
  const [invoicesFor, setInvoicesFor] = useState<JobListEntry | null>(null);
  const [invoiceDraft, setInvoiceDraft] = useState("");
  // Custom tasks first, because starting one and finishing one are what
  // a lead comes here to do several times a day.
  const [tab, setTab] = useState<"custom" | "types">("custom");
  const [confirming, setConfirming] = useState<string | null>(null);

  // Derived above the handlers that read them, not below.
  // Only a custom task can be started: generic ones are seeded
  // rows that never complete, and a retired one is retired.
  const startable = taskTypes.filter((t) => t.kind === "JOB" && t.active);
  // Retiring the task that is selected has to clear the selection. The
  // option disappears from the list, but the id stayed in state, so the
  // form still had something to submit and the server was the only
  // thing that noticed.
  const selected = startable.some((t) => t.id === taskTypeId) ? taskTypeId : "";
  const retiredCount = taskTypes.filter((t) => !t.active).length;
  const listed = showRetired ? taskTypes : taskTypes.filter((t) => t.active);
  const generic = listed.filter((t) => t.kind === "STANDING");
  const custom = listed.filter((t) => t.kind === "JOB");
  const anySheet = Boolean(seenBy || typeSheet || starting || invoicesFor);

  const load = useCallback(async () => {
    try {
      // One catalogue fetch, not two. The starting form used to read
      // /api/task-types while the list read /api/manage/task-types, so
      // a task retired in the list stayed startable until a reload.
      const [{ taskTypes: list }, l, j] = await Promise.all([
        api.manageTaskTypes(),
        api.locations(),
        api.jobs(),
      ]);
      setTaskTypes(list);
      setLocations(l.locations);
      setJobs(j.jobs);
    } catch {
      setError("Could not load.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** True when the work succeeded, so a sheet knows whether to close. */
  const run = async (work: () => Promise<void>, whenFailed: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await work();
      await load();
      return true;
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      // A chain of ternaries got unreadable once renaming added a
      // fourth case. The server's error codes are the keys.
      const known: Record<string, string> = {
        name_taken: "That name is already taken.",
        name_required: "A name cannot be blank.",
        task_type_not_found: "That task is no longer in the catalogue.",
        task_type_is_standing: "A generic task is not started here.",
        location_not_found: "That location is no longer in use.",
        // A lead pastes a column out of the invoice sheet, so these two
        // are things they can fix — not "could not do it".
        invoice_too_long: "One of those invoice numbers is too long.",
        too_many_invoices: "That is more invoice numbers than one task can carry.",
        job_not_found: "That task is no longer open.",
        already_closed: "Somebody has already finished it.",
        job_is_standing: "A generic task has no invoice numbers.",
      };
      setError(known[code] ?? whenFailed);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openNewType = () => {
    setError(null);
    setTypeForm({ name: "", kind: typeForm.kind, description: "" });
    setTypeSheet({ mode: "new" });
  };

  const openEditType = (task: ManagedTaskType) => {
    setError(null);
    setTypeForm({ name: task.name, kind: task.kind, description: task.description ?? "" });
    setTypeSheet({ mode: "edit", task });
  };

  const saveType = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!typeSheet || !typeForm.name.trim()) return;
    if (typeSheet.mode === "edit") {
      const ok = await run(async () => {
        await api.updateTaskType(typeSheet.task.id, {
          name: typeForm.name.trim(),
          description: typeForm.description.trim() || null,
        });
      }, "Could not save.");
      if (ok) setTypeSheet(null);
      return;
    }
    const wanted = typeForm.kind;
    let revivedNotice: string | null = null;
    const ok = await run(async () => {
      const { taskType, revived } = await api.addTaskType(
        typeForm.name.trim(),
        wanted,
        typeForm.description.trim() || undefined,
      );
      // Adding a name that is already retired revives the original, so
      // its sessions stay attached to one row. The kind comes back with
      // it and is deliberately not changed — a task that changed shape
      // halfway through makes its own numbers incomparable. Both of
      // those are silent unless this says so, and since retired tasks
      // are hidden by default there is nothing on screen to infer it
      // from.
      if (revived) {
        const kindChanged = taskType.kind !== wanted;
        revivedNotice =
          `“${taskType.name}” was retired and is back, with everything ` +
          `already recorded against it.` +
          (kindChanged
            ? ` It stays ${
                taskType.kind === "STANDING" ? "generic" : "custom"
              }, because a task cannot change kind — retire it and add a ` +
              `replacement under another name instead.`
            : "");
      }
    }, "Could not add the task.");
    if (ok) {
      setTypeSheet(null);
      // After run(), which clears the notice before it starts.
      if (revivedNotice) setNotice(revivedNotice);
    }
  };

  const openStart = () => {
    setError(null);
    setCreated(null);
    setStarting(true);
  };

  const start = (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    void run(async () => {
      const { job } = await api.createJob(
        selected,
        locationId || undefined,
        splitInvoices(invoiceText),
      );
      // Shown large because it is the only place this code appears
      // until somebody prints it: the tag is no longer queued
      // automatically, and the code has to be writable onto a tag by
      // hand when the printer is out of labels.
      setCreated(job.label ? { id: job.id, label: job.label } : null);
      setInvoiceText("");
    }, "Could not start the task.");
  };

  const finish = (job: JobListEntry) =>
    void run(async () => {
      await api.completeJob(job.id);
      setConfirming(null);
      onJobsChanged();
    }, "Could not finish the task.");

  const openInvoices = (job: JobListEntry) => {
    setError(null);
    setInvoiceDraft(job.invoices.join("\n"));
    setInvoicesFor(job);
  };

  const saveInvoices = (event: React.FormEvent) => {
    event.preventDefault();
    if (!invoicesFor) return;
    const job = invoicesFor;
    void run(async () => {
      await api.setJobInvoices(job.id, splitInvoices(invoiceDraft));
      setInvoicesFor(null);
    }, "Could not save the invoice numbers.");
  };

  const labelPdf = (jobId: string) => `/api/jobs/${encodeURIComponent(jobId)}/label.pdf`;
  const closeSeenBy = () => {
    setSeenBy(null);
    setPeople(null);
  };

  const typeRow = (task: ManagedTaskType) => (
    <li key={task.id} className={task.active ? "" : "retired"}>
      <div className="item-text">
        <span className="item-title">
          {task.name}
          {!task.active ? <span className="badge">retired</span> : null}
        </span>
        {task.description ? <span className="item-meta">{task.description}</span> : null}
      </div>
      <span className="row-actions">
        <button
          type="button"
          className="ghost"
          disabled={busy}
          onClick={() => openEditType(task)}
        >
          Edit
        </button>
        <button
          type="button"
          className="ghost"
          disabled={busy}
          onClick={() =>
            void run(
              () =>
                api.updateTaskType(task.id, { active: !task.active }).then(() => undefined),
              "Could not change the task.",
            )
          }
        >
          {task.active ? "Retire" : "Restore"}
        </button>
        {/* Admin only, and only for generic tasks — custom work is
            scanned or picked off a list, so it has no tile to scope. The
            server says the same about the route; this only stops offering
            a button that would answer 403. */}
        {me.role === "ADMIN" && task.kind === "STANDING" && task.active ? (
          <button
            type="button"
            className="ghost"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                setSeenBy(task);
                setPeople(null);
                const { people } = await api.taskVisibility(task.id);
                setPeople(people);
              }, "Could not load who sees this.")
            }
          >
            Who sees this
          </button>
        ) : null}
      </span>
    </li>
  );

  const sheetError = error ? <p className="error" role="alert">{error}</p> : null;

  return (
    <main>
      <header className="title-with-back">
        <button type="button" className="ghost" onClick={onBack}>
          <Icon name="back" size={26} />
          Back
        </button>
        <h1>Tasks</h1>
      </header>

      <nav className="tabs" aria-label="Task sections">
        <button type="button" aria-pressed={tab === "custom"} onClick={() => setTab("custom")}>
          Custom tasks
        </button>
        <button type="button" aria-pressed={tab === "types"} onClick={() => setTab("types")}>
          Task types
        </button>
      </nav>

      {/* Inside the sheet instead while one is open — behind the overlay
          an error is a failure nobody sees. */}
      {error && !anySheet ? <p className="error" role="alert">{error}</p> : null}
      {notice ? <p className="notice" role="status">{notice}</p> : null}

      {tab === "custom" ? (
        <>
          {startable.length === 0 ? (
            <p className="muted">
              Nothing to start. Add a task type with the Custom kind first —
              generic tasks are picked up off the floor screen instead, and
              never start from here.
            </p>
          ) : (
            <button type="button" className="primary wide" onClick={openStart} disabled={busy}>
              <Icon name="play" size={26} />
              Create a task
            </button>
          )}

          <section className="card">
            <h2>Open</h2>
            {jobs.length === 0 ? (
              <p className="muted">Nothing open.</p>
            ) : (
              <ul className="joblist">
                {jobs.map((job) => (
                  <li key={job.id} className={confirming === job.id ? "confirming" : ""}>
                    <code>{job.label}</code>
                    <div className="item-text">
                      <span className="item-title">{job.taskType.name}</span>
                      <span className="item-meta">
                        {[
                          job.location?.name,
                          job.invoices.length > 0 ? job.invoices.join(" ") : null,
                          // How many, never who. The spec's non-goal made
                          // concrete: a screen leads look at all day is
                          // exactly where a per-person view would install
                          // itself by accident.
                          job.activeEmployees > 0
                            ? `${job.activeEmployees} working`
                            : job.status === "IN_PROGRESS"
                              ? "started"
                              : "waiting",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>

                    {/* Confirmation is inline rather than a native confirm().
                        Finishing is irreversible and stops other people's
                        timers, so it needs a deliberate second tap — but a
                        browser dialog puts small buttons in the middle of a
                        screen meant to be used with gloves at arm's length. */}
                    {confirming === job.id ? (
                      <div className="row row-full">
                        <span className="error">
                          {job.activeEmployees > 0
                            ? `Finish? Will stop ${job.activeEmployees} running timer(s).`
                            : "Finish? This cannot be undone."}
                        </span>
                        <span className="row-actions">
                          <button
                            type="button"
                            className="ghost"
                            onClick={() => setConfirming(null)}
                            disabled={busy}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="danger"
                            onClick={() => finish(job)}
                            disabled={busy}
                          >
                            Confirm
                          </button>
                        </span>
                      </div>
                    ) : (
                      <span className="row-actions">
                        {/*
                          The only way to get a tag, since 2026-09-24. A plain link
                          rather than a fetch: the browser's own download handling puts
                          the file where the lead can find it, and it carries the
                          session cookie without this code touching a token. Printing
                          it is the lead's business and any printer will do — this app
                          drives none.
                        */}
                        <a className="button-link" href={labelPdf(job.id)} download>
                          <Icon name="download" size={20} />
                          PDF
                        </a>
                        <button
                          type="button"
                          className="ghost"
                          onClick={() => openInvoices(job)}
                          disabled={busy}
                        >
                          Invoices
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          onClick={() => setConfirming(job.id)}
                          disabled={busy}
                        >
                          Finish
                        </button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : (
        <>
          <button type="button" className="primary wide" onClick={openNewType} disabled={busy}>
            Add a type
          </button>

          {/* Two lists, not one with a label on each row. Which kind a
              type is decides everything about it — whether it is a tile
              or is started here — so it is the first thing the screen
              sorts by. */}
          <section className="card">
            <div>
              <h2>Generic</h2>
              <p className="hint">Tiles on the floor screen, picked up by anyone.</p>
            </div>
            {generic.length === 0 ? (
              <p className="muted">None yet.</p>
            ) : (
              <ul className="joblist">{generic.map(typeRow)}</ul>
            )}
          </section>

          <section className="card">
            <div>
              <h2>Custom</h2>
              <p className="hint">Started under Custom tasks, one at a time, each with its own tag.</p>
            </div>
            {custom.length === 0 ? (
              <p className="muted">None yet.</p>
            ) : (
              <ul className="joblist">{custom.map(typeRow)}</ul>
            )}
          </section>

          {/* Retiring a task while they are hidden makes the row vanish,
              which is exactly what a delete looks like. The count is the
              proof it is still there, so it appears the moment there is
              one to show. */}
          {retiredCount > 0 ? (
            <label className="list-toggle">
              <input
                type="checkbox"
                checked={showRetired}
                onChange={(e) => setShowRetired(e.target.checked)}
              />
              Show retired tasks ({retiredCount})
            </label>
          ) : null}

          <p className="hint">
            Retiring a task takes it off the list. Everything already
            recorded against it stays.
          </p>
        </>
      )}

      {starting ? (
        <Sheet title="Create a task" onClose={() => setStarting(false)}>
          {sheetError}
          {created ? (
            <>
              <div className="created" role="status">
                <div>
                  <p className="muted">Label for the cart</p>
                  <code className="huge">{created.label}</code>
                </div>
                {/* The same file as the PDF link on the Open list, here too
                    because this is the moment somebody wants it: the tag is
                    no longer queued automatically, so a task started and
                    not printed has no tag at all. */}
                <a className="button-link primary" href={labelPdf(created.id)} download>
                  <Icon name="download" />
                  Tag PDF
                </a>
              </div>
              <div className="sheet-actions">
                <button type="button" className="ghost" onClick={() => setCreated(null)}>
                  Start another
                </button>
                <span className="spacer" />
                <button type="button" className="primary" onClick={() => setStarting(false)}>
                  Done
                </button>
              </div>
            </>
          ) : (
            <form className="stack" onSubmit={start}>
              <div className={locations.length > 0 ? "pair" : undefined}>
                <label>
                  Which task
                  <select value={selected} onChange={(e) => setTaskTypeId(e.target.value)} required>
                    <option value="">— choose —</option>
                    {startable.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>

                {/* The shop has one site, so this only appears if someone has
                    actually set locations up. Where work happens is normally
                    said in the task's own description. */}
                {locations.length > 0 ? (
                  <label>
                    Location
                    <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                      <option value="">— none —</option>
                      {locations.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>

              <label>
                Invoice numbers
                <textarea
                  value={invoiceText}
                  onChange={(e) => setInvoiceText(e.target.value)}
                  rows={3}
                  placeholder="One per line, or separated by commas"
                  aria-label="Invoice numbers"
                />
              </label>
              {/* Said here rather than discovered later: these are the join
                  key against the shop's invoice sheet, and a number that
                  does not match it joins to nothing without saying so. */}
              <p className="hint">
                These link the hours to the invoice sheet. They can be
                corrected while the task is open.
              </p>

              <button type="submit" className="primary wide" disabled={busy || !selected}>
                Start
              </button>
            </form>
          )}
        </Sheet>
      ) : null}

      {invoicesFor ? (
        <Sheet
          title="Correct invoice numbers"
          subtitle={`${invoicesFor.label ?? ""} · ${invoicesFor.taskType.name}`}
          onClose={() => setInvoicesFor(null)}
        >
          {sheetError}
          <form className="stack" onSubmit={saveInvoices}>
            <label>
              Invoice numbers
              <textarea
                value={invoiceDraft}
                onChange={(e) => setInvoiceDraft(e.target.value)}
                rows={4}
                placeholder="One per line, or separated by commas"
                aria-label="Invoice numbers"
              />
            </label>
            <p className="hint">
              These replace the numbers stored for this cart. Leave it empty
              to remove them all.
            </p>
            <button type="submit" className="primary wide" disabled={busy}>
              Save
            </button>
          </form>
        </Sheet>
      ) : null}

      {typeSheet ? (
        <Sheet
          title={typeSheet.mode === "new" ? "Add a type" : `Edit ${typeSheet.task.name}`}
          onClose={() => setTypeSheet(null)}
        >
          {sheetError}
          <form className="stack" onSubmit={(e) => void saveType(e)}>
            <label>
              Name
              <input
                type="text"
                value={typeForm.name}
                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                placeholder="Name"
                aria-label="Task name"
                autoFocus
              />
            </label>
            {typeSheet.mode === "new" ? (
              <label>
                Kind
                {/* The words alone, no description after them: the shop's
                    call on 2026-09-26 that "generic" and "custom" say
                    enough, and a sentence per option made the menu wider
                    than the field on a tablet. What each kind does is in
                    README "The two ways work is tracked". */}
                <select
                  value={typeForm.kind}
                  onChange={(e) =>
                    setTypeForm({ ...typeForm, kind: e.target.value as "JOB" | "STANDING" })
                  }
                >
                  <option value="STANDING">Generic</option>
                  <option value="JOB">Custom</option>
                </select>
              </label>
            ) : (
              <p className="hint">
                {typeSheet.task.kind === "STANDING" ? "Generic" : "Custom"}. The kind
                cannot change — retire this and add a replacement instead.
              </p>
            )}
            <label>
              Description
              <input
                type="text"
                value={typeForm.description}
                onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                placeholder="Where it happens, what it covers"
                aria-label="Task description"
              />
            </label>
            <button
              type="submit"
              className="primary wide"
              disabled={busy || !typeForm.name.trim()}
            >
              {typeSheet.mode === "new" ? "Add task type" : "Save"}
            </button>
          </form>
        </Sheet>
      ) : null}

      {seenBy ? (
        <Sheet
          title={`Who sees ${seenBy.name}`}
          subtitle={
            people
              ? `${people.filter((p) => !p.hidden).length} of ${people.length} people`
              : undefined
          }
          onClose={closeSeenBy}
        >
          {sheetError}
          {people === null ? (
            <p className="muted">Loading…</p>
          ) : (
            <>
              <ul className="checklist three">
                {people.map((row) => (
                  <li key={row.employeeId}>
                    <label className={row.hidden ? "off" : undefined}>
                      <input
                        type="checkbox"
                        checked={!row.hidden}
                        disabled={busy}
                        onChange={() =>
                          setPeople((cur) =>
                            (cur ?? []).map((r) =>
                              r.employeeId === row.employeeId ? { ...r, hidden: !r.hidden } : r,
                            ),
                          )
                        }
                      />
                      <span>{row.displayName}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <p className="hint">
                A tick means that person sees this tile.{" "}
                <strong>
                  Leave somebody ticked if there is any chance they do this
                  work
                </strong>{" "}
                — with nowhere to record it they tap the nearest tile instead,
                and nothing on any screen would show that. The same setting per
                person is under <strong>Admin → People → Activities</strong>.
              </p>
            </>
          )}
          <div className="sheet-actions">
            <button
              type="button"
              className="ghost"
              disabled={busy || people === null}
              onClick={() => setPeople((cur) => (cur ?? []).map((r) => ({ ...r, hidden: false })))}
            >
              Everyone
            </button>
            {/* The efficient path for a task only a few people do: clear the
                board, then tick them. It is also the direction that can
                leave somebody with nowhere to record their work, which is
                what the note above says. */}
            <button
              type="button"
              className="ghost"
              disabled={busy || people === null}
              onClick={() => setPeople((cur) => (cur ?? []).map((r) => ({ ...r, hidden: true })))}
            >
              Nobody
            </button>
            <span className="spacer" />
            <button
              type="button"
              className="primary"
              disabled={busy || people === null}
              onClick={() =>
                void run(async () => {
                  const hidden = (people ?? []).filter((p) => p.hidden).map((p) => p.employeeId);
                  const out = await api.setTaskVisibility(seenBy.id, hidden);
                  setPeople(out.people);
                }, "Could not save who sees this.")
              }
            >
              Save
            </button>
          </div>
        </Sheet>
      ) : null}
    </main>
  );
}
